// src/pages/LoginPage.js
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BrandFooter from '../components/BrandFooter';
import { logoPng } from '../config/assets';
import { STAFF_PWA_COURIER, STAFF_PWA_KEY } from '../lib/staffPwa';
import { DEMO_ACCOUNTS } from '../config/demo';

export default function LoginPage({ onLogin }) {
  const [searchParams] = useSearchParams();
  const courierHint = searchParams.get('hint') === 'courier';
  const demoMode = searchParams.get('demo') === '1';
  const [email, setEmail] = useState(demoMode ? DEMO_ACCOUNTS.admin.email : '');
  const [password, setPassword] = useState(demoMode ? DEMO_ACCOUNTS.admin.password : '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const flash = sessionStorage.getItem('auth_flash');
      if (flash) {
        setError(flash);
        sessionStorage.removeItem('auth_flash');
      }
    } catch (_) { /* no-op */ }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/login', { email, password });
      try {
        if (data.role === 'courier') {
          localStorage.setItem(STAFF_PWA_KEY, STAFF_PWA_COURIER);
        } else {
          localStorage.removeItem(STAFF_PWA_KEY);
        }
      } catch (_) { /* no-op */ }
      onLogin(data.token, {
        id: data.id,
        role: data.role,
        restaurant_id: data.restaurant_id,
        email: data.email,
        name: data.name,
        is_super_admin: !!data.is_super_admin,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка входа');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.pageCenter}>
        <div style={s.card}>
          <div style={s.logo}>
            <img src={logoPng()} alt="" style={s.logoImg} width={64} height={64} />
          </div>
          <h1 style={s.title}>Вход в систему</h1>
          <p style={s.sub}>Управление рестораном</p>
          {demoMode && (
            <p style={s.hint}>
              <strong>Demo.</strong> Админ: {DEMO_ACCOUNTS.admin.email} / {DEMO_ACCOUNTS.admin.password}
              <br />
              Кухня: {DEMO_ACCOUNTS.kitchen.email} · Курьер: {DEMO_ACCOUNTS.courier.email} — тот же пароль.
              Оплата в demo не списывает.
            </p>
          )}
          {courierHint && !demoMode && (
            <p style={s.hint}>
              <strong>Курьер:</strong> введите тот же email и пароль, что заданы в админке → «Персонал» → курьеры. Если
              не входит — владелец может нажать ✏️ у курьера и задать новый пароль.
            </p>
          )}

          {error && <div style={s.error}>{error}</div>}

          <form onSubmit={handleSubmit} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Email</label>
              <input
                style={s.input}
                type="email"
                value={email}
                autoFocus
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@restaurant.com"
                required
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Пароль</label>
              <input
                style={s.input}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <button style={s.btn} type="submit" disabled={loading}>
              {loading ? 'Вход...' : 'Войти'}
            </button>
            <p style={s.waiterLink}>
              <Link to="/waiter-login" style={{ color: '#007AFF', textDecoration: 'none', fontSize: '14px' }}>
                Вход официанта
              </Link>
            </p>
          </form>
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f5f5f7',
    fontFamily: '-apple-system, sans-serif',
    paddingTop: 'max(0px, env(safe-area-inset-top, 0px))',
    paddingBottom: 'max(0px, env(safe-area-inset-bottom, 0px))',
  },
  pageCenter: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '24px',
    padding: '40px 32px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  logo: { textAlign: 'center', marginBottom: '16px' },
  logoImg: { display: 'block', margin: '0 auto', objectFit: 'contain' },
  title: { fontSize: '24px', fontWeight: '700', textAlign: 'center', margin: '0 0 4px' },
  sub: { fontSize: '14px', color: '#888', textAlign: 'center', margin: '0 0 32px' },
  hint: {
    fontSize: '12px',
    lineHeight: 1.45,
    color: '#1B5E20',
    backgroundColor: '#E8F5E9',
    padding: '10px 12px',
    borderRadius: '10px',
    margin: '0 0 16px',
    textAlign: 'left',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '500', color: '#444' },
  input: {
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    fontSize: '15px',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
  },
  waiterLink: { textAlign: 'center', margin: '4px 0 0' },
  btn: {
    padding: '14px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  error: {
    padding: '12px',
    borderRadius: '10px',
    backgroundColor: '#FFEBEE',
    color: '#E85D4A',
    fontSize: '14px',
    textAlign: 'center',
  },
};
